"use client";

import { useEffect, useRef, useState } from "react";

/**
 * REVEAL — animasi halus saat elemen masuk viewport.
 *
 * Pakai:
 *   <Reveal>...</Reveal>
 *   <Reveal delay={120}>...</Reveal>           // efek berurutan
 *   <Reveal direction="left">...</Reveal>      // up (default) | left | right | none
 *   <Reveal duration={1400}>...</Reveal>       // lebih lambat lagi
 *   <Reveal distance={80}>...</Reveal>         // jarak gerak, px
 *
 * Yang membuat terasa halus (bukan cuma durasi):
 *  - jarak gerak. 56px terbaca jauh lebih lambat dari 32px pada durasi sama
 *  - easing cubic-bezier(0.16, 1, 0.3, 1): melesat di awal, mengerem panjang
 *  - hanya opacity & transform yang dianimasikan, jadi tidak memicu reflow
 *
 * Catatan:
 *  - Animasi hanya jalan sekali. Elemen yang muncul-hilang berulang saat
 *    scroll balik terasa mengganggu, bukan halus.
 *  - Menghormati "prefers-reduced-motion": konten langsung tampil.
 *  - Jangan dipakai di hero / AddressBar — konten paling atas harus langsung
 *    terlihat, dan menganimasikannya merusak metrik LCP.
 *  - Kalau JS gagal dimuat, isi Reveal tetap opacity-0. Jangan bungkus
 *    informasi kritis (alamat, nomor IGD, jam operasional).
 */
export default function Reveal({
  children,
  delay = 0,
  duration = 1100,
  distance = 56,
  direction = "up",
  className = "",
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      // rootMargin negatif: animasi mulai sedikit setelah elemen masuk layar,
      // supaya tidak selesai duluan sebelum pengguna sempat melihatnya.
      { threshold: 0.1, rootMargin: "0px 0px -80px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const offset = {
    up: `translate3d(0, ${distance}px, 0)`,
    left: `translate3d(-${distance}px, 0, 0)`,
    right: `translate3d(${distance}px, 0, 0)`,
    none: "none",
  }[direction];

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translate3d(0, 0, 0)" : offset,
        transitionProperty: "opacity, transform",
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
        transitionDelay: shown ? `${delay}ms` : "0ms",
        willChange: shown ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}
